import { MutationType, useCreateMutation } from 'generated/gql';
import {
  Button,
  List,
  ListItem,
  Modal,
  ModalActions,
  ModalContent,
  ModalHeader,
  Text,
} from '@rulebooks/evidend-titan';
import { t, Trans } from '@lingui/macro';
import { formatMutationTypeLink } from 'helpers/formatMutation';
import { useLinkRegisterToGroup } from 'hooks/useLinkRegisterToGroup';
import { useRouter } from 'next/router';
import React from 'react';
import { incorporationSteps } from 'wizards/Incorporation';

interface IIncorporationStartModal {
  isModalShown: boolean;
  onModalClose: () => any;
  legalEntityId?: string;
  /** When set, this management group is made the owner of the created register. */
  groupId?: string;
}

export const IncorporationStartModal = ({
  isModalShown,
  onModalClose,
  legalEntityId,
  groupId,
}: IIncorporationStartModal) => {
  const router = useRouter();
  const [createMutation] = useCreateMutation();
  const linkRegisterToGroup = useLinkRegisterToGroup();

  const createLegalEntity = async (mutationType: MutationType) => {
    if (legalEntityId) {
      createMutation({
        variables: {
          legalEntityId,
          mutationType,
        },
        onCompleted: async ({ createMutation }) => {
          if (groupId) await linkRegisterToGroup(createMutation.register.id, groupId);
          router.push({
            pathname: `/${formatMutationTypeLink(mutationType)}/${createMutation.id}`,
          });
        },
        onError: (error) => {
          console.error(error);
        },
      });
    } else {
      router.push({
        pathname: `/${formatMutationTypeLink(mutationType)}/aanmaken/${
          incorporationSteps.LegalEntityCreateSelect
        }`,
        ...(groupId && { query: { groupId } }),
      });
    }
  };

  return (
    <Modal isShown={isModalShown} size="m" onClose={onModalClose}>
      <ModalHeader title={t`Register aanmaken`} />
      <ModalContent>
        <Text color="grey80" spaceAfter={4} type="paragraph">
          <Trans>In Evidend kun je op twee manieren een register aanmaken:</Trans>
        </Text>
        <List spaceAfter={4} type="ol">
          <ListItem prefixAdornment="•">
            <Trans>alle mutaties opvoeren vanaf oprichtingsdatum</Trans>
          </ListItem>
          <ListItem prefixAdornment="•">
            <Trans>
              de verdeling van aandelen op een bepaalde datum en eventuele mutaties vanaf die datum
              opvoeren
            </Trans>
          </ListItem>
        </List>
        <Text color="grey80" type="paragraph">
          <Trans>Wat wil je gebruiken als startpunt?</Trans>
        </Text>
      </ModalContent>
      <ModalActions>
        <Button variant="clear" onClick={() => createLegalEntity(MutationType.Onboarding)}>
          <Trans>Andere datum</Trans>
        </Button>
        <Button variant="primary" onClick={() => createLegalEntity(MutationType.Incorporation)}>
          <Trans>Oprichtingsdatum</Trans>
        </Button>
      </ModalActions>
    </Modal>
  );
};
